import { useEffect, useState } from "react";
import { api, errorMessage } from "../../api";
import type { Manifest, OrgSettings } from "../../api/types";
import { ErrorBox, Spinner } from "../../components/bits";
import { isKnownTimeZone, TimeZoneSelect } from "../../components/TimeZoneSelect";
import { connectorLabel } from "../../lib/format";
import { checkSession, toast } from "../../store/app";
import { Forbidden, useOrgData } from "./shared";

export function SettingsTab() {
  const overview = useOrgData((id) => api.admin.overview(id));
  const [form, setForm] = useState<OrgSettings | null>(null);
  const [connectors, setConnectors] = useState<Manifest[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (overview.data) setForm(overview.data.settings);
  }, [overview.data]);
  useEffect(() => {
    api
      .connectors()
      .then((r) => setConnectors(r.connectors))
      .catch(() => setConnectors([]));
  }, []);

  if (overview.forbidden) return <Forbidden />;
  if (overview.error && !overview.data) return <ErrorBox error={overview.error} onRetry={overview.reload} />;
  if (!form || !overview.orgId) return <Spinner />;

  const set = (p: Partial<OrgSettings>) => setForm((f) => (f ? { ...f, ...p } : f));
  const allowed = form.allowed_connectors;
  const toggleConnector = (type: string, on: boolean) => {
    const cur = new Set(allowed ?? connectors.map((c) => c.connector_type));
    if (on) cur.add(type);
    else cur.delete(type);
    set({ allowed_connectors: [...cur] });
  };

  return (
    <section aria-labelledby="orgset-title" className="admin-section">
      <h2 id="orgset-title">Organization settings</h2>
      <form
        className="form settings-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!isKnownTimeZone(form.default_timezone)) {
            setError("Choose a default time zone from the list.");
            return;
          }
          setBusy(true);
          setError(null);
          try {
            const saved = await api.admin.patchSettings(overview.orgId!, form);
            setForm(saved);
            toast("Settings saved", "success");
            if (saved.name !== overview.data?.settings.name) void checkSession();
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span>Organization name</span>
          <input value={form.name} required onChange={(e) => set({ name: e.target.value })} />
        </label>
        <TimeZoneSelect
          label="Default time zone"
          value={form.default_timezone}
          onChange={(default_timezone) => set({ default_timezone })}
        />
        <p className="muted small">Used for members who have not chosen their own zone and for server-side date rendering.</p>
        <fieldset className="field">
          <legend>Access</legend>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.open_registration}
              onChange={(e) => set({ open_registration: e.target.checked })}
            />
            Open registration – anyone who can reach this instance may create an account and join as member
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.allow_user_connections}
              onChange={(e) => set({ allow_user_connections: e.target.checked })}
            />
            Allow members to add personal connections
          </label>
        </fieldset>
        <fieldset className="field">
          <legend>Allowed connectors</legend>
          <label className="check-row">
            <input type="radio" name="allowed" checked={allowed === null} onChange={() => set({ allowed_connectors: null })} />
            All connectors
          </label>
          <label className="check-row">
            <input
              type="radio"
              name="allowed"
              checked={allowed !== null}
              onChange={() => set({ allowed_connectors: connectors.map((c) => c.connector_type) })}
            />
            Only selected:
          </label>
          <div className="connector-checks">
            {connectors.map((c) => (
              <label key={c.connector_type} className="check-row">
                <input
                  type="checkbox"
                  disabled={allowed === null}
                  checked={allowed === null || allowed.includes(c.connector_type)}
                  onChange={(e) => toggleConnector(c.connector_type, e.target.checked)}
                />
                {(c.display_name as string | undefined) ?? connectorLabel(c.connector_type)}
                <span className="muted small">{c.connector_type}</span>
              </label>
            ))}
            {connectors.length === 0 && <span className="muted small">No connectors installed.</span>}
          </div>
        </fieldset>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions start">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Saving…" : "Save settings"}
          </button>
          <button type="button" className="btn" onClick={() => overview.data && setForm(overview.data.settings)}>
            Reset
          </button>
        </div>
      </form>
    </section>
  );
}
